import { html } from '@shared/utils/rendering/html';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { GenerateLoginConfig } from '../../configs/authentication/login.config';
import { LoaderWithTitle } from '@shared/components/loading/loader-with-title';
import ErmLoginScreen from '@shared/components/navigation/landing-page/login-screen.erm';
import { useLocation } from 'preact-iso';
import { useFeatureChecks } from '../../utils/features/feature-context';
import { useAuth } from '../../utils/context/auth-context';

const LoginPage = () => {
    const { route, query } = useLocation();
    const { reloadUser } = useAuth();

    const returnUrl = query.returnUrl;

    const { useFeature } = useFeatureChecks();

    const featureCheck = useCallback(
        (key, fallback) => {
            return useFeature(key, fallback);
        },
        [useFeature]
    );

    const [config, setConfig] = useState(null);
    const loginFormRef = useRef(null);

    const internalLoginCallback = useCallback(async () => {
        const { username, password } = loginFormRef?.current?.value ?? {};
        try {
            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ username, password }),
            });

            if (!response.ok) {
                return 'Invalid username or password';
            }

            // The login just set the session cookie, but the auth context only checked the session
            // once when the app started (before there was one). Refresh it first; otherwise the
            // landing page's auth guard still sees "not signed in" and hard-redirects back to /Login,
            // so the first sign-in appears to fail and only a second attempt gets through.
            await reloadUser();

            route('/');
        } catch (err) {
            console.error('Login failed', err);
            return 'Something went wrong during login';
        }
    }, [returnUrl, route, reloadUser]);

    useEffect(() => {
        const overrides = {
            internalLogin: {
                callback: internalLoginCallback,
            },
            externalLogin: {
                callback: () => {
                    window.location.href =
                        '/api/auth/external-login' +
                        (returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '');
                },
            },
        };
        setConfig(GenerateLoginConfig(overrides, featureCheck));
    }, [internalLoginCallback, featureCheck, returnUrl]);

    return config
        ? html`<${ErmLoginScreen} ref=${loginFormRef} config=${config} />`
        : html`<${LoaderWithTitle} busyLoading=${true} title="Please wait" />`;
};

export default LoginPage;
